/**
 * Iter 129 (Feb 2026) — Impersonation banner + helpers.
 *
 * When an admin/manager clicks "Log in as" on an employee row, we
 * back up the caller's current auth state to sessionStorage (so a
 * hard refresh mid-impersonation still knows how to exit), swap in
 * the impersonation JWT, and mount this banner on top of every page.
 *
 * The banner:
 *   • Shows "Viewing as {name} · admin session preserved" in yellow
 *   • Offers a single "Exit impersonation" button that restores the
 *     original JWT + navigates back to /admin
 *
 * Keys used:
 *   pff_auth_token           — active JWT (portal AuthContext reads this)
 *   pff_admin_token          — X-Admin-Token for /admin API calls
 *   pff_impersonation_backup — { admin_jwt, admin_token, target } (session)
 */
import { useEffect, useState } from "react";
import { ArrowLeft, Eye, X } from "lucide-react";

const BACKUP_KEY = "pff_impersonation_backup";

export function isImpersonating() {
  try { return !!sessionStorage.getItem(BACKUP_KEY); }
  catch { return false; }
}

export function readBackup() {
  try {
    const raw = sessionStorage.getItem(BACKUP_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function writeBackup(payload) {
  try { sessionStorage.setItem(BACKUP_KEY, JSON.stringify(payload)); }
  catch { /* private mode */ }
}

export function clearBackup() {
  try { sessionStorage.removeItem(BACKUP_KEY); }
  catch { /* ignore */ }
}

/**
 * Called from the Admin Employees row. Handles the whole swap:
 *   1. POST /admin/employees/{id}/impersonate with the admin's JWT
 *   2. Back up admin's current tokens
 *   3. Overwrite active tokens with the target's JWT
 *   4. Navigate to /portal/employee
 */
export async function beginImpersonation(axios, apiBase, employeeId) {
  const adminJwt = (() => {
    try { return localStorage.getItem("pff_auth_token") || ""; }
    catch { return ""; }
  })();
  const adminToken = (() => {
    try { return localStorage.getItem("pff_admin_token") || ""; }
    catch { return ""; }
  })();
  const adminViewerRole = (() => {
    try { return localStorage.getItem("pff_viewer_role") || ""; }
    catch { return ""; }
  })();

  const { data } = await axios.post(
    `${apiBase}/admin/employees/${employeeId}/impersonate`,
    {},
    { headers: { Authorization: `Bearer ${adminJwt}` } },
  );

  writeBackup({
    admin_jwt: adminJwt,
    admin_token: adminToken,
    admin_viewer_role: adminViewerRole,
    target: {
      id: data.user.id,
      name: data.user.full_name || data.user.email,
      role: data.user.role,
    },
    started_at: new Date().toISOString(),
  });

  try {
    localStorage.setItem("pff_auth_token", data.access_token);
    // Clear the static admin token so /admin routes don't leak the
    // impersonator's admin powers into the swapped session.
    localStorage.removeItem("pff_admin_token");
    // Iter 136 — clear the viewer role so AdminPage re-derives it
    // from the impersonated JWT (manager vs admin) on next mount.
    localStorage.removeItem("pff_viewer_role");
  } catch { /* localStorage blocked */ }

  return data;
}

export function endImpersonation(navigate) {
  const backup = readBackup();
  if (backup?.admin_jwt) {
    try { localStorage.setItem("pff_auth_token", backup.admin_jwt); }
    catch { /* ignore */ }
  }
  if (backup?.admin_token) {
    try { localStorage.setItem("pff_admin_token", backup.admin_token); }
    catch { /* ignore */ }
  }
  // Iter 143 — restore the admin's viewer role too. Without this,
  // AdminPage boots up with the impersonated manager's `pff_viewer_role`
  // still in localStorage and hides admin-only tabs (Employees, Apply
  // Links) even though the admin JWT is back in place.
  if (backup?.admin_viewer_role) {
    try { localStorage.setItem("pff_viewer_role", backup.admin_viewer_role); }
    catch { /* ignore */ }
  } else {
    // Legacy backups don't have this key — clear it so AdminPage
    // re-derives it via /admin/exchange-token on next mount.
    try { localStorage.removeItem("pff_viewer_role"); }
    catch { /* ignore */ }
  }
  clearBackup();
  // Full navigation resets any in-memory portal caches AND re-arms
  // the AuthContext with the restored JWT.
  if (typeof window !== "undefined") {
    window.location.href = "/admin";
  } else if (navigate) {
    navigate("/admin");
  }
}

/** Yellow persistent banner. Mounted at the app shell. */
export default function ImpersonationBanner() {
  const [backup, setBackup] = useState(readBackup);

  useEffect(() => {
    // Re-check on route changes / storage events so the banner
    // appears + disappears without a hard reload.
    const check = () => setBackup(readBackup());
    window.addEventListener("storage", check);
    // Poll every 800ms as a safety net — sessionStorage doesn't
    // fire 'storage' events for same-tab changes.
    const iv = setInterval(check, 800);
    return () => {
      window.removeEventListener("storage", check);
      clearInterval(iv);
    };
  }, []);

  if (!backup?.target) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] bg-amber-400 text-amber-950 border-b-2 border-amber-500 shadow-md"
      data-testid="impersonation-banner"
      role="alert"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="w-4 h-4 shrink-0" strokeWidth={2.4} />
          <p className="text-sm font-semibold truncate">
            Viewing as{" "}
            <span className="font-bold" data-testid="impersonation-target-name">
              {backup.target.name}
            </span>
            <span className="hidden sm:inline text-amber-800 font-normal ml-2">
              · admin session preserved
            </span>
          </p>
        </div>
        <button
          onClick={() => endImpersonation()}
          data-testid="impersonation-exit"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-950 text-amber-100 text-xs font-bold hover:bg-black transition-colors shrink-0"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Exit impersonation
        </button>
      </div>
    </div>
  );
}
